import React, {useEffect, useState} from 'react';
import {Select,Tabs} from 'antd';
import { useTranslation } from 'react-i18next';
import {useHashState} from 'utils/hashState';
import PricingSchemeTab from "./components/PricingSchemeTab";
import {
    useFilteredPricingScenarios,
    BUSINESS_SCENARIO_DSP,
    BUSINESS_SCENARIO_ORDER,
    BUSINESS_SCENARIO_COST_PERIODIC,
} from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import PricingSchemeApplyPage from "./components/apply/PricingSchemeApplyPage";
import DSPPricingApplyPage from "./components/apply/dsp/DSPPricingApplyPage";
import { DSPPricingPage } from "./components/dsp";
import CostPeriodicPricingPage from "./components/periodic/CostPeriodicPricingPage";
import CostPeriodicApplyPage from "./components/apply/periodic/CostPeriodicApplyPage";
import { hasResource } from 'utils/resourceAuth';
import { ResourceCode } from 'constants/resourceCodes';


const PricingMainPage: React.FC = () => {
    const { t } = useTranslation();
    const hasRevenueSetupAccess         = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_PLAN_CHECK]);
    const hasRevenuePriceApplyAccess    = hasResource([ResourceCode.settlement.SETTLEMENT_REVENUE_PRICE_APPLY_CHECK]);
    const hasCostSetupAccess            = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PRICE_PLAN_CHECK]);
    const hasCostApplyAccess            = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PRICE_APPLY_CHECK]);
    const hasCostPeriodicSetupAccess    = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_PLAN_CHECK]);
    const hasCostPeriodicApplyAccess    = hasResource([ResourceCode.settlement.SETTLEMENT_COST_PERIODIC_PRICE_APPLY_CHECK]);

    const hasRevenuePriceAccess    = hasRevenueSetupAccess || hasRevenuePriceApplyAccess;
    const hasCostPriceAccess       = hasCostSetupAccess    || hasCostApplyAccess;
    const hasCostPeriodicAccess    = hasCostPeriodicSetupAccess || hasCostPeriodicApplyAccess;
    const hasSetupAccess           = hasRevenueSetupAccess || hasCostSetupAccess || hasCostPeriodicSetupAccess;
    const hasApplyAccess           = hasRevenuePriceApplyAccess || hasCostApplyAccess || hasCostPeriodicApplyAccess;

    const [activeTab, setActiveTab] = useHashState({tab: 'setup'});
    const [selectedBusinessScenario, setSelectedBusinessScenario] = useState<number | null>(null);

    const { scenarioOptions, loading: scenarioLoading } = useFilteredPricingScenarios(hasRevenuePriceAccess, hasCostPriceAccess, hasCostPeriodicAccess);

    // 当业务场景列表加载完成且当前没有选择时，默认选择第一个
    useEffect(() => {
        if (scenarioOptions.length > 0 && selectedBusinessScenario === null) {
            setSelectedBusinessScenario(scenarioOptions[0].value);
        }
    }, [scenarioOptions, selectedBusinessScenario]);

    // 处理业务场景选择变化
    const handleBusinessScenarioChange = (value: number) => {
        // 不同业务场景的筛选组件共用同名 hash 字段（如 bindTime、timezone），切换业务场景时
        // 清掉 URL hash 里除 tab 外的其它字段，避免带着上一个业务场景的筛选条件进入新场景
        window.history.replaceState(null, '', `#tab=${encodeURIComponent(activeTab.tab)}`);
        setSelectedBusinessScenario(value);
    };

    // 根据业务场景获取对应的价格配置组件，需显式校验对应资源码
    const getPricingSetupContent = () => {
        if (selectedBusinessScenario == BUSINESS_SCENARIO_DSP && hasCostSetupAccess) {
            return <DSPPricingPage businessScenario={selectedBusinessScenario} />;
        }
        if (selectedBusinessScenario == BUSINESS_SCENARIO_ORDER && hasRevenueSetupAccess) {
            return <PricingSchemeTab businessScenario={selectedBusinessScenario} />;
        }
        if (selectedBusinessScenario == BUSINESS_SCENARIO_COST_PERIODIC && hasCostPeriodicSetupAccess) {
            return <CostPeriodicPricingPage businessScenario={selectedBusinessScenario} />;
        }
        return null;
    };

    const getPricingApplyContent = () => {
        if (selectedBusinessScenario == BUSINESS_SCENARIO_DSP && hasCostApplyAccess) {
            return <DSPPricingApplyPage businessScenario={selectedBusinessScenario} />;
        }
        if (selectedBusinessScenario == BUSINESS_SCENARIO_ORDER && hasRevenuePriceApplyAccess) {
            return <PricingSchemeApplyPage businessScenario={selectedBusinessScenario} />;
        }
        if (selectedBusinessScenario == BUSINESS_SCENARIO_COST_PERIODIC && hasCostPeriodicApplyAccess) {
            return <CostPeriodicApplyPage businessScenario={selectedBusinessScenario} />;
        }
        return null;
    };

    const items = [];
    if (hasSetupAccess) {
        items.push({
            label: t('settlement.priceCenter.tabs.pricePlan'),
            key: 'setup',
            children: getPricingSetupContent(),
        });
    }
    if (hasApplyAccess) {
        items.push({
            label: t('settlement.priceCenter.tabs.priceApply'),
            key: 'apply',
            children: getPricingApplyContent(),
        });
    }

    // URL 里的 tab 无权限访问时不强制跳转，返回 undefined 不选中任何 tab
    const effectiveTab = (() => {
        if (activeTab.tab === 'setup' && hasSetupAccess) return 'setup';
        if (activeTab.tab === 'apply' && hasApplyAccess) return 'apply';
        return undefined;
    })();

    const operations = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
            <span>业务场景：</span>
            <Select
                value={selectedBusinessScenario || undefined}
                style={{ width: 180 }}
                size="large"
                placeholder="请选择业务场景"
                options={scenarioOptions}
                onChange={handleBusinessScenarioChange}
                loading={scenarioLoading}
            />
        </div>
    )


    return (
        <div className="bg-white rounded-lg shadow-sm p-8">
            <Tabs 
                tabBarExtraContent={operations} 
                items={items} 
                size='large' 
                activeKey={effectiveTab}
                onChange={(key) => setActiveTab({tab: key})} 
                destroyInactiveTabPane={true}
            />
        </div>
    );
};

export default PricingMainPage;